/**
 * Comet's adapter for T3 Code's BranchToolbar.
 *
 * T3 calls these values environment, workspace, and branch. Comet calls the
 * same concrete target a Space: a project checkout on a particular device.
 * Keeping that translation here means the rest of the UI can retain T3's
 * context-strip structure without inventing a second routing model.
 */

import { useEffect, useState, type ReactNode } from "react";
import type { Space } from "../lib/models";
import { groupSpacesByRepository, spaceLabel, type RepositoryGroup } from "../lib/models";
import { IconBranch, IconChevron, IconDevice, IconFolder, IconFolderGit, IconServer } from "./icons";

type Device = { id: string; name?: string | null; platform?: string };

export function BranchToolbar({
  space,
  spaces,
  devices,
  refs,
  worktree,
  baseRef,
  disabled = false,
  onSpaceChange,
  onWorktreeChange,
  onBaseRefChange
}: {
  space: Space | undefined;
  spaces: readonly Space[];
  devices: readonly Device[];
  refs: readonly string[];
  worktree: boolean;
  baseRef: string | undefined;
  disabled?: boolean;
  onSpaceChange?: (spaceId: string) => void;
  onWorktreeChange?: (value: boolean) => void;
  onBaseRefChange?: (value: string | undefined) => void;
}) {
  const [open, setOpen] = useState<"environment" | "workspace" | "branch" | null>(null);
  const [groups, setGroups] = useState<RepositoryGroup[]>([]);
  const [branchQuery, setBranchQuery] = useState("");

  useEffect(() => setGroups(groupSpacesByRepository(spaces)), [spaces]);
  useEffect(() => setOpen(null), [space?.id]);

  const device = space === undefined ? undefined : devices.find((candidate) => candidate.id === space.deviceId);
  const environmentLabel = device?.name ?? device?.id ?? "Choose environment";
  const branchLabel = baseRef ?? (worktree ? "Choose base ref" : "Current checkout");
  const filteredRefs = refs.filter((ref) => ref.toLowerCase().includes(branchQuery.trim().toLowerCase()));

  return (
    <div className="relative mx-auto -mt-4 flex w-[calc(100%-2.75rem)] items-center gap-1 overflow-visible rounded-b-2xl border border-t-0 border-[var(--chat-composer-outline)] bg-card px-2 pt-5 pb-1 text-xs text-muted-foreground shadow-[var(--shadow-composer)]">
      <ContextButton
        disabled={disabled || onSpaceChange === undefined}
        icon={<IconServer className="size-3.5 shrink-0" />}
        label={environmentLabel}
        onClick={() => setOpen(open === "environment" ? null : "environment")}
      />
      {open === "environment" && onSpaceChange !== undefined ? (
        <Popup className="left-0 w-72">
          <PopupLabel>Run on</PopupLabel>
          {groups.map((group) => (
            <div key={group.key} className="px-1 pb-1">
              <p className="px-2 py-1 text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                {group.label}
              </p>
              {group.spaces.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  onClick={() => {
                    onSpaceChange(candidate.id);
                    setOpen(null);
                  }}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs hover:bg-accent ${candidate.id === space?.id ? "bg-accent text-foreground" : ""}`}
                >
                  <IconDevice className="size-3.5 shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{deviceLabel(candidate.deviceId, devices)}</span>
                  <span className="max-w-28 truncate text-2xs text-muted-foreground/70">{candidate.path}</span>
                </button>
              ))}
            </div>
          ))}
          {groups.length === 0 ? <p className="px-2 py-2 text-xs">No environments available.</p> : null}
        </Popup>
      ) : null}

      <span className="h-4 w-px bg-border" aria-hidden="true" />
      <ContextButton
        disabled={disabled || onWorktreeChange === undefined}
        icon={worktree ? <IconFolderGit className="size-3.5 shrink-0" /> : <IconFolder className="size-3.5 shrink-0" />}
        label={worktree ? "New worktree" : "Current checkout"}
        onClick={() => setOpen(open === "workspace" ? null : "workspace")}
      />
      {open === "workspace" && onWorktreeChange !== undefined ? (
        <Popup className="left-20 w-48">
          <PopupLabel>Workspace</PopupLabel>
          <PopupItem active={!worktree} onClick={() => { onWorktreeChange(false); setOpen(null); }}>Current checkout</PopupItem>
          <PopupItem active={worktree} onClick={() => { onWorktreeChange(true); setOpen(null); }}>New worktree</PopupItem>
        </Popup>
      ) : null}

      {space?.gitDetected === true ? (
        <>
          <span className="h-4 w-px bg-border" aria-hidden="true" />
          <ContextButton
            disabled={disabled || onBaseRefChange === undefined}
            icon={<IconBranch className="size-3.5 shrink-0" />}
            label={branchLabel}
            onClick={() => setOpen(open === "branch" ? null : "branch")}
          />
          {open === "branch" && onBaseRefChange !== undefined ? (
            <Popup className="right-0 w-64">
              <PopupLabel>Branch</PopupLabel>
              <input
                autoFocus
                value={branchQuery}
                onChange={(event) => setBranchQuery(event.target.value)}
                placeholder="Search branches"
                aria-label="Search branches"
                className="mx-1 mb-1 h-8 w-[calc(100%-0.5rem)] rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-ring"
              />
              <PopupItem active={baseRef === undefined} onClick={() => { onBaseRefChange(undefined); setOpen(null); }}>Current checkout</PopupItem>
              {filteredRefs.map((ref) => (
                <PopupItem key={ref} active={baseRef === ref} onClick={() => { onBaseRefChange(ref); setOpen(null); }}>
                  {ref}
                </PopupItem>
              ))}
              {filteredRefs.length === 0 && refs.length > 0 ? <p className="px-2 py-2 text-2xs">No matching branches.</p> : null}
            </Popup>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function ContextButton({
  icon,
  label,
  disabled,
  onClick
}: {
  icon: ReactNode;
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex h-7 min-w-0 max-w-[48%] items-center gap-1 rounded-md border border-transparent px-1.5 text-left text-xs font-normal text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-70"
    >
      {icon}
      <span className="min-w-0 truncate">{label}</span>
      <IconChevron className="size-3 shrink-0 opacity-50" />
    </button>
  );
}

function Popup({ className, children }: { className: string; children: ReactNode }) {
  return <div className={`absolute top-9 z-30 max-h-72 overflow-y-auto rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lg ${className}`}>{children}</div>;
}

function PopupLabel({ children }: { children: ReactNode }) {
  return <p className="px-2 py-1.5 text-2xs font-medium text-muted-foreground">{children}</p>;
}

function PopupItem({ children, active, onClick }: { children: ReactNode; active: boolean; onClick: () => void }) {
  return <button type="button" onClick={onClick} className={`flex w-full items-center rounded-lg px-2 py-1.5 text-left text-xs hover:bg-accent ${active ? "bg-accent text-foreground" : "text-muted-foreground"}`}>{children}</button>;
}

const deviceLabel = (id: string, devices: readonly Device[]): string => devices.find((device) => device.id === id)?.name ?? id;

export function SpaceContext({ space, devices }: { space: Space | undefined; devices: readonly Device[] }) {
  if (space === undefined) return null;
  return <span className="inline-flex min-w-0 items-center gap-1 text-2xs text-muted-foreground"><IconDevice className="size-3 shrink-0" />{deviceLabel(space.deviceId, devices)} · {spaceLabel(space)}</span>;
}
